import Big from 'big.js';
import * as buttons from 'smg/buttons';
import { LineItemMap, SizeMap, TypeMap } from 'smg/customer-api/lineitems';
import { DownloadOrderInvoice } from 'smg/customer-api/v2/orders';
import { ShowError } from 'smg/errors';
import * as models from 'smg/customer-api/models';
import { OrderAggregates, Orders } from 'smg/customer-api/orders';
import { GetProofURLs } from 'smg/proofs';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';

import * as carts from '../carts';
import { Relref } from '../hugo';
import { AccountReady } from '../account-session';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

void main();

// ── Status derivation ──

type orderStatus = 'awaiting' | 'preparing' | 'production' | 'shipped' | 'delivered';

function deriveStatus(order: models.HyejooOrder): orderStatus {
    const hasShipments = order.shipmentsCount > 0 || order.markedAsShipped;
    if (hasShipments && order.shipmentsToCustomerNotDeliveredCount === 0) {
        return 'delivered';
    }
    if (hasShipments || order.shipmentsToCustomerNotDeliveredCount > 0) {
        return 'shipped';
    }
    if (order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_CONFIRMED) {
        return 'production';
    }
    if (order.confirmedTime) {
        return 'preparing';
    }
    if (order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_UNCONFIRMED) {
        return 'awaiting';
    }
    return 'preparing';
}

const statusBadge: Record<orderStatus, { cls: string; label: string }> = {
    awaiting: { cls: 's-await', label: 'Awaiting payment' },
    preparing: { cls: 's-prep', label: 'Preparing' },
    production: { cls: 's-prod', label: 'In production' },
    shipped: { cls: 's-ship', label: 'Shipped' },
    delivered: { cls: 's-deliv', label: 'Delivered' },
};

// ── Hyejoo filter queries (matching Plus v2 patterns) ──

async function main() {
    if (await AccountReady) {
        return;
    }
    const statusFilters: Record<string, string> = {
        all: '',
        awaiting: 'balance:>.05',
        production: `orderStatusID:${models.OrderStatusID.ORDER_STATUSES_CONFIRMED} hasShipments:false balance:<.05`,
        shipped: 'shipmentsToCustomerNotDeliveredCount:>0 balance:<.05',
        delivered: 'flagIDs:"Delivered" shipmentsToCustomerNotDeliveredCount:0',
    };
    const PAGE_SIZE = 10;
    const DATA_FIELDS = [
        'invoiceNumber,created,orderLineItems,orderProofs,orderShippingOptions,total,balance,orderStatusID,key',
        'markedAsShipped,confirmedTime,shipmentsCount,shipmentsToCustomerNotDeliveredCount',
    ].join(',');
    const $filterTabs = document.querySelector('#order-filter-tabs') as HTMLElement;
    const $searchForm = document.querySelector('#order-search-form') as HTMLFormElement;
    const $searchInput = document.querySelector('#order-search-input') as HTMLInputElement;
    const $sort = document.querySelector('#order-sort') as HTMLSelectElement;
    const $count = document.querySelector('#order-count') as HTMLElement;
    const $list = document.querySelector('#order-list') as HTMLElement;
    const $skeleton = document.querySelector('#order-skeleton') as HTMLElement;
    const $emptyNone = document.querySelector('#order-empty-none') as HTMLElement;
    const $emptyFilter = document.querySelector('#order-empty-filter') as HTMLElement;
    const $emptyFilterTitle = document.querySelector('#empty-filter-title') as HTMLElement;
    const $emptyFilterCta = document.querySelector('#empty-filter-cta') as HTMLAnchorElement;
    const $emptySearch = document.querySelector('#order-empty-search') as HTMLElement;
    const $emptySearchTitle = document.querySelector('#empty-search-title') as HTMLElement;
    const $emptySearchCta = document.querySelector('#empty-search-cta') as HTMLAnchorElement;
    const $pagination = document.querySelector('#order-pagination') as HTMLElement;

    // ── State from URL ──

    const qs = QueryString();
    let currentStatus = qs.get('status') || 'all';
    let currentSearch = qs.get('q') || '';
    let currentSort = qs.get('sort') || 'newest';
    let currentPage = parseInt(qs.get('page') || '1', 10);

    $searchInput.value = currentSearch;
    $sort.value = currentSort;
    setActiveTab(currentStatus);

    // ── Fetch tab counts ──

    function fetchTabCounts() {
        for (const [key, q] of Object.entries(statusFilters)) {
            void OrderAggregates(q, ['id']).then(resp => {
                const count = resp.aggregates.get('id')?.count ?? 0;
                const $tab = $filterTabs.querySelector(`[data-status="${key}"] .num`) as HTMLElement | null;
                if ($tab) {
                    $tab.textContent = `(${count})`;
                }
            }).catch(() => { /* counts are non-critical */ });
        }
    }

    fetchTabCounts();

    // ── Load orders ──

    async function loadOrders() {
        showSkeleton();

        const filterQ = statusFilters[currentStatus] || '';
        const searchQ = currentSearch ? `invoiceNumber:${currentSearch}` : '';
        const q = [filterQ, searchQ].filter(Boolean).join(' ') || undefined;

        try {
            const params: Record<string, string | number> = {
                'sort': currentSort === 'oldest' ? 'created asc' : 'created desc',
                'max': PAGE_SIZE,
                'p': currentPage - 1,
                'data': DATA_FIELDS,
            };
            if (q) {
                params['q'] = q;
            }
            const resp = await Orders(params);
            const orders = resp.results;

            if (orders.length === 0 && currentPage === 1) {
                if (currentSearch) {
                    showEmpty('search');
                } else if (currentStatus === 'all') {
                    showEmpty('none');
                } else {
                    showEmpty('filter');
                }
                return;
            }

            // Render cards
            $list.innerHTML = '';
            const f = document.createDocumentFragment();
            for (const order of orders) {
                f.appendChild(await renderOrderCard(order));
            }
            $list.appendChild(f);

            // Count
            if (orders.length < PAGE_SIZE && currentPage === 1) {
                $count.textContent = `${orders.length} order${orders.length === 1 ? '' : 's'}`;
            } else {
                $count.textContent = '';
            }

            renderPagination(resp.nextPage);
            showContent();
        } catch {
            showContent();
            $list.innerHTML = '<div class="text-center text-muted py-4">Something went wrong loading orders.</div>';
        }
    }

    // ── Render order card ──

    async function renderOrderCard(order: models.HyejooOrder): Promise<HTMLElement> {
        const el = template.Clone('#order-card-template').firstElementChild as HTMLElement;
        const status = deriveStatus(order);
        const badge = statusBadge[status];

        // Header
        const $num = el.querySelector('.order-card-num') as HTMLElement;
        $num.textContent = `Order #${order.invoiceNumber ?? ''}`;

        const $date = el.querySelector('.date-text') as HTMLElement;
        if (order.created?.isValid) {
            $date.textContent = order.created.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' });
        }

        const $badge = el.querySelector('.status-badge') as HTMLElement;
        $badge.className = `status-badge ${badge.cls}`;
        $badge.textContent = badge.label;

        // Line items (stacked divs with inline qty pill)
        const $items = el.querySelector('.order-card-items') as HTMLElement;
        if (order.orderLineItems?.length) {
            $items.appendChild(await renderCompactLineItems(order.orderLineItems));
        }

        // Proofs
        const $proofs = el.querySelector('.order-card-proofs') as HTMLElement;
        if (order.orderProofs?.length) {
            $proofs.classList.remove('d-none');
            const fancyboxGroup = `order-${order.id ?? ''}`;
            for (const op of order.orderProofs) {
                const proof = op.proof;
                if (!proof) {
                    continue;
                }
                const urls = GetProofURLs(window.cloudFront, proof.file || '');
                const $link = document.createElement('a');
                $link.href = urls.main;
                $link.setAttribute('data-fancybox', fancyboxGroup);
                const $img = document.createElement('img');
                $img.className = 'order-card-proof';
                $img.src = urls.thumb || urls.main;
                $img.alt = op.name || 'Proof';
                $img.loading = 'lazy';
                $link.appendChild($img);
                $proofs.appendChild($link);
            }
        }

        // Footer — total + balance
        const $total = el.querySelector('.order-card-total') as HTMLElement;
        $total.textContent = DollarFormat(order.total || 0);

        const balance = Big(order.balance || 0);
        if (balance.gt(0.05)) {
            const $balance = el.querySelector('.order-card-balance') as HTMLElement;
            $balance.classList.remove('d-none');
            $balance.textContent = `${DollarFormat(balance)} due`;
        }

        // Actions
        const $actions = el.querySelector('.order-card-actions') as HTMLElement;
        const orderKey = order.key || order.id || '';
        const orderUrl = `${Relref('/order-detail')}?key=${encodeURIComponent(orderKey)}`;

        if (status === 'awaiting' && balance.gt(0.05)) {
            const $pay = document.createElement('a');
            $pay.className = 'btn btn-primary btn-sm';
            $pay.href = orderUrl;
            $pay.textContent = 'Make payment';
            $actions.appendChild($pay);
        }

        // Order again (all statuses)
        {
            const $reorder = document.createElement('button');
            $reorder.className = 'btn btn-link btn-sm';
            $reorder.type = 'button';
            $reorder.textContent = 'Order again';
            carts.CartToggle({
                $btn: $reorder,
                type: 'order',
                id: order.id ?? '',
                key: orderKey,
                addLabel: 'Order again',
            });
            $actions.appendChild($reorder);
        }

        const $invoice = document.createElement('button');
        $invoice.type = 'button';
        $invoice.className = 'btn btn-link btn-sm';
        $invoice.innerHTML = '<i class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px">download</i>Invoice';
        $invoice.addEventListener('click', () => {
            buttons.Start($invoice);
            void DownloadOrderInvoice(order.key ? `key:${order.key}` : `id:${order.id ?? ''}`).catch(ShowError).finally(() => buttons.Stop($invoice));
        });
        $actions.appendChild($invoice);

        const $view = document.createElement('a');
        $view.className = 'btn btn-outline-primary btn-sm';
        $view.href = orderUrl;
        $view.textContent = 'View order';
        $actions.appendChild($view);

        return el;
    }

    // ── Compact line items (stacked divs with inline qty pill) ──

    async function renderCompactLineItems(items: models.OrderLineItem[]): Promise<HTMLElement> {
        const $container = document.createElement('div');
        $container.className = 'order-card-lineitems';

        for (const item of items) {
            const itemParts = [
                (await LineItemMap()).get(item.lineItemID || '')?.networkNamePlural() || item.lineItem,
                (await TypeMap()).get(item.lineItemTypeID || '')?.networkName() || item.type,
                (await SizeMap()).get(item.lineItemTypeSizeID || '')?.networkName() || item.size,
                item.description,
            ].filter(Boolean);

            const $parent = document.createElement('div');
            $parent.className = 'li-parent';
            $parent.innerHTML = `<span class="li-name">${escHtml(itemParts.join(' \u00b7 '))}</span><span class="li-qty">\u00d7${(item.quantity || 0).toLocaleString()}</span>`;
            $container.appendChild($parent);

            for (const child of item.orderChildLineItems || []) {
                const childParts = [child.child, child.option, child.description].filter(Boolean);
                if (!childParts.length) {
                    continue;
                }
                const $child = document.createElement('div');
                $child.className = 'li-child';
                if (child.quantity && child.quantity !== item.quantity) {
                    $child.innerHTML = `<span class="li-name">${escHtml(childParts.join(' \u00b7 '))}</span><span class="li-qty">\u00d7${child.quantity.toLocaleString()}</span>`;
                } else {
                    $child.innerHTML = `<span class="li-name">${escHtml(childParts.join(' \u00b7 '))}</span>`;
                }
                $container.appendChild($child);
            }
        }

        return $container;
    }

    // ── Pagination ──

    function renderPagination(hasNextPage: boolean) {
        $pagination.innerHTML = '';
        if (currentPage === 1 && !hasNextPage) {
            $pagination.classList.add('d-none');
            return;
        }
        $pagination.classList.remove('d-none');

        const $prev = document.createElement('button');
        $prev.className = 'account-pag-btn';
        $prev.innerHTML = '<i class="material-icons" style="font-size:14px">chevron_left</i>';
        $prev.disabled = currentPage <= 1;
        $prev.addEventListener('click', () => goToPage(currentPage - 1));
        $pagination.appendChild($prev);

        const $btn = document.createElement('button');
        $btn.className = 'account-pag-btn active';
        $btn.textContent = String(currentPage);
        $pagination.appendChild($btn);

        const $next = document.createElement('button');
        $next.className = 'account-pag-btn';
        $next.innerHTML = '<i class="material-icons" style="font-size:14px">chevron_right</i>';
        $next.disabled = !hasNextPage;
        $next.addEventListener('click', () => goToPage(currentPage + 1));
        $pagination.appendChild($next);
    }

    function goToPage(page: number) {
        currentPage = page;
        syncURL();
        window.scrollTo({ top: 0 });
        void loadOrders();
    }

    // ── UI state helpers ──

    let skeletonTimer: ReturnType<typeof setTimeout> | undefined;

    function showSkeleton() {
        clearTimeout(skeletonTimer);
        $emptyNone.classList.add('d-none');
        $emptyFilter.classList.add('d-none');
        $emptySearch.classList.add('d-none');
        $pagination.classList.add('d-none');
        $count.textContent = '';
        skeletonTimer = setTimeout(() => {
            $list.innerHTML = '';
            $skeleton.classList.remove('d-none');
        }, 500);
    }

    function showContent() {
        clearTimeout(skeletonTimer);
        $skeleton.classList.add('d-none');
        $emptyNone.classList.add('d-none');
        $emptyFilter.classList.add('d-none');
        $emptySearch.classList.add('d-none');
    }

    function showEmpty(type: 'none' | 'filter' | 'search') {
        clearTimeout(skeletonTimer);
        $skeleton.classList.add('d-none');
        $list.innerHTML = '';
        $pagination.classList.add('d-none');
        $count.textContent = '';
        $emptyNone.classList.toggle('d-none', type !== 'none');
        $emptyFilter.classList.toggle('d-none', type !== 'filter');
        $emptySearch.classList.toggle('d-none', type !== 'search');

        if (type === 'filter') {
            const label = statusBadge[currentStatus as orderStatus]?.label || currentStatus;
            $emptyFilterTitle.textContent = `No ${label.toLowerCase()} orders`;
        }
        if (type === 'search') {
            $emptySearchTitle.textContent = `No results for \u201c${currentSearch}\u201d`;
        }
    }

    function setActiveTab(status: string) {
        for (const $tab of Array.from($filterTabs.querySelectorAll('.account-filter-tab'))) {
            const isActive = $tab.getAttribute('data-status') === status;
            $tab.classList.toggle('active', isActive);
        }
    }

    function syncURL() {
        const params = new URLSearchParams();
        if (currentStatus !== 'all') {
            params.set('status', currentStatus);
        }
        if (currentSearch) {
            params.set('q', currentSearch);
        }
        if (currentSort !== 'newest') {
            params.set('sort', currentSort);
        }
        if (currentPage > 1) {
            params.set('page', String(currentPage));
        }
        const url = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        history.replaceState(null, '', url);
    }

    // ── Event handlers ──

    $filterTabs.addEventListener('click', e => {
        const $tab = (e.target as HTMLElement).closest('.account-filter-tab') as HTMLElement | null;
        if (!$tab) {
            return;
        }
        e.preventDefault();
        const status = $tab.getAttribute('data-status') || 'all';
        if (status === currentStatus) {
            return;
        }
        currentStatus = status;
        currentSearch = '';
        currentPage = 1;
        $searchInput.value = '';
        setActiveTab(status);
        syncURL();
        void loadOrders();
    });

    $searchForm.addEventListener('submit', e => {
        e.preventDefault();
        currentSearch = $searchInput.value.trim();
        currentPage = 1;
        syncURL();
        void loadOrders();
    });

    $sort.addEventListener('change', () => {
        currentSort = $sort.value;
        currentPage = 1;
        syncURL();
        void loadOrders();
    });

    $emptyFilterCta.addEventListener('click', e => {
        e.preventDefault();
        currentStatus = 'all';
        currentPage = 1;
        setActiveTab('all');
        syncURL();
        void loadOrders();
    });

    $emptySearchCta.addEventListener('click', e => {
        e.preventDefault();
        currentSearch = '';
        currentPage = 1;
        $searchInput.value = '';
        syncURL();
        void loadOrders();
    });

    // ── Initial load ──
    await loadOrders();
}

function escHtml(s: string): string {
    const el = document.createElement('span');
    el.textContent = s;
    return el.innerHTML;
}
